import { object } from 'prop-types';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { AddressForm, Card, DashboardContext, FormToggle, Skeleton, UserForm } from 'components';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

const DasbhoardInformations = () => {
  const { currentUser } = useUser();
  const { user } = useContext(DashboardContext);

  const { t } = useTranslation();
  const { call: addressesCall, loading: addressesLoading, data: addresses } = useFetch();
  const { call: newsletterCall, loading: newsletterLoading } = useFetch();

  const { setValue, getValues, register, formState } = useForm({
    mode: 'all',
    defaultValues: {
      select: false
    }
  });

  const {
    register: registerDomicile,
    handleSubmit: handleSubmitDomicile,
    getValues: getValuesDomicile,
    setValue: setValueDomicile,
    formState: formStateDomicile,
    control: controlDomicile,
    clearErrors: clearErrorsDomicile
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      civilityDomicile: '',
      name: '',
      firstname: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      postalCode: '',
      sameAddress: false
    }
  });

  const {
    register: registerLivraison,
    handleSubmit: handleSubmitLivraison,
    getValues: getValuesLivraison,
    setValue: setValueLivraison,
    formState: formStateLivraison,
    control: controlLivraison,
    clearErrors: clearErrorsLivraison
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      civilityLivraison: '',
      name: '',
      firstname: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      postalCode: '',
      sameAddress: false
    }
  });

  useEffect(() => {
    getAddresses();
  }, []);

  useEffect(() => {
    if (user) setValue('select', user.newsLetter);
  }, [user]);

  const getAddresses = () => {
    if (!currentUser?.serialNumber) return;

    addressesCall({
      service: 'castelis',
      path: `/users/addresses/${currentUser.serialNumber}`,
      method: 'get'
    });
  };

  const copyAddress = () => {
    // Copy all values from domicile form to livraison form
    for (const [key, value] of Object.entries(getValuesDomicile())) {
      setValueLivraison(key, value);
    }
  };

  const onNewsletterChange = () => {
    if (!currentUser?.serialNumber || newsletterLoading) return;

    newsletterCall({
      service: 'castelis',
      path: `/users/newsletter/${currentUser.serialNumber}`,
      method: 'post',
      query: {
        select: getValues('select')
      }
    });
  };

  const getFormAddress = (type) => addresses?.addresses?.find((address) => address.type === type);

  const providerValues = {
    register,
    getValues,
    setValue,
    formState
  };

  const domicileProviderValues = {
    register: registerDomicile,
    handleSubmit: handleSubmitDomicile,
    getValues: getValuesDomicile,
    setValue: setValueDomicile,
    formState: formStateDomicile,
    control: controlDomicile,
    clearErrors: clearErrorsDomicile
  };

  const livraisonProviderValues = {
    register: registerLivraison,
    handleSubmit: handleSubmitLivraison,
    getValues: getValuesLivraison,
    setValue: setValueLivraison,
    formState: formStateLivraison,
    control: controlLivraison,
    clearErrors: clearErrorsLivraison
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 mt-8">
      {addressesLoading && (
        <>
          <Skeleton type="text" classNames="h-96 w-full col-span-12 rounded-lg lg:col-span-7" />
          <Skeleton type="text" classNames="h-96 w-full col-span-12 rounded-lg lg:col-span-5" />
          <div className="col-span-12 lg:col-span-6">
            <Skeleton type="text" classNames="h-96 w-full rounded-lg" />
          </div>
          <div className="col-span-12 lg:col-span-6">
            <Skeleton type="text" classNames="h-96 w-full rounded-lg" />
          </div>
        </>
      )}

      {!addressesLoading && (
        <>
          {user && <UserForm userInfos={user} />}
          <Card className="order-1 lg:order-none lg:col-span-5 lg:h-fit" onMobile={false}>
            <>
              <h2 className="h2 underlined-yellow mb-8">{t('form:newsletter.title')}</h2>
              <FormProvider {...providerValues}>
                <form onChange={onNewsletterChange}>
                  <FormToggle
                    name="select"
                    labeli18nKey="form:newsletter.label"
                    disabled={newsletterLoading}
                    className="mb-8"
                  />
                </form>
              </FormProvider>
              <p className="font-bold text-gray-600 mb-8">
                {t('form:newsletter.warning.firstParagraph', { brandName: appService.brand() })}
              </p>

              <Trans i18nKey={'form:newsletter.warning.secondParagraph'} values={{ brandName: appService.brand() }}>
                <NavLink to={routes.terms.path} className="underline hover:no-underline">
                  {t('form:newsletter.warning.click')}
                </NavLink>
              </Trans>
            </>
          </Card>

          <div className="lg:col-span-6">
            <FormProvider {...domicileProviderValues}>
              <AddressForm
                address={getFormAddress('Domicile')}
                addressType="Domicile"
                title={t('form:addresses.billing.title')}
                copyAddress={copyAddress}
              />
            </FormProvider>
          </div>

          <div className="lg:col-span-6">
            <FormProvider {...livraisonProviderValues}>
              <AddressForm
                address={getFormAddress('Livraison')}
                addressType="Livraison"
                title={t('form:addresses.delivery.title')}
                copyAddress={copyAddress}
              />
            </FormProvider>
          </div>
        </>
      )}
    </div>
  );
};

DasbhoardInformations.propTypes = {
  user: object
};
export default DasbhoardInformations;
